import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import MessageInfoSchema from "notifications/entities/message-info/message-info-schema";
import NotificationSchema from "notifications/entities/notification/notification-schema";
import { z } from "zod";

export type MessageNotificationJSON = z.infer<typeof MessageNotificationSchema.MessageNotificationJSONZ>;
export type MessageNotificationParams = z.infer<typeof MessageNotificationSchema.MessageNotificationZ>;

export default class MessageNotificationSchema {
  public static readonly MessageNotificationJSONZ = z.object({
    ...NotificationSchema.NotificationJSONZ.shape,
    messageInfo: MessageInfoSchema.MessageInfoJSONZ.nullish(),
  });
  public static readonly MessageNotificationZ = z.object({
    ...NotificationSchema.NotificationZ.shape,
    messageInfo: MessageInfoSchema.MessageInfoZ.optional(),
  });

  public static validate(values: unknown): MessageNotificationParams {
    return parseOrThrow(MessageNotificationSchema.MessageNotificationZ, values, {
      logError: true,
      schemaName: "MessageNotificationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): MessageNotificationJSON {
    return parseOrThrow(MessageNotificationSchema.MessageNotificationJSONZ, values, {
      logError: true,
      schemaName: "MessageNotificationSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is MessageNotificationParams {
    try {
      MessageNotificationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is MessageNotificationJSON {
    try {
      MessageNotificationSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: MessageNotificationJSON): MessageNotificationParams {
    const parsed = parseOrThrow(MessageNotificationSchema.MessageNotificationJSONZ, data, {
      logError: true,
      schemaName: "MessageNotificationSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return MessageNotificationSchema.codec.decode(parsed);
  }

  public static serialize(topic: MessageNotificationParams): MessageNotificationJSON {
    return MessageNotificationSchema.codec.encode(topic);
  }

  private static readonly codec = z.codec(
    MessageNotificationSchema.MessageNotificationJSONZ,
    MessageNotificationSchema.MessageNotificationZ,
    {
      decode: (data) => {
        const { messageInfo, ...notificationData } = data;

        const base = NotificationSchema.deserialize(notificationData);

        if (!base?.id || !base?.createdDate || !base.recipients) {
          throw new Error("Invalid data: Missing required Notification fields.");
        }

        return {
          ...base,
          messageInfo: messageInfo ? MessageInfoSchema.deserialize(messageInfo) : undefined,
        };
      },
      encode: (messageNotification) => {
        // Remove messageInfo before passing to NotificationSchema
        const { messageInfo, ...notificationData } = messageNotification;

        const baseJSON = NotificationSchema.serialize(notificationData);

        if (!baseJSON?.id || !baseJSON?.createdDate || !baseJSON.recipients) {
          throw new Error("Invalid data: Missing required Notification fields.");
        }

        return {
          ...baseJSON,
          messageInfo: messageInfo ? MessageInfoSchema.serialize(messageInfo) : undefined,
        };
      },
    }
  );
}
